import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import { BugAttachments } from './BugAttachments';
import ChatView from '../../../next/chat/ChatView';
import { validateBugChat } from '../../../services/next/chatService';
import { colors } from '../../../theme/tokens';

/* The tester's read-only view of one of their bugs, with the bug's comment thread. Shown in the
   Reported bugs drawer (_components/accounts/tester/bugs/TesterBugsList). */

/* Label above a light box holding the text, so long steps stay readable */
const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.titleBlue, mb: 0.5 }}>{label}</Typography>
    <Typography
      sx={{ fontSize: 15, lineHeight: 1.6, color: value ? colors.textDark : colors.textGrey, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        backgroundColor: '#F7F9FC', border: `1px solid ${colors.dividerBlue}`, borderRadius: 1.5, px: 1.75, py: 1.25 }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const SectionTitle: React.FC = ({ children }) => (
  <Typography component="h3" sx={{ fontSize: 17, fontWeight: 600, color: colors.titleBlue, mb: 1.5 }}>
    {children}
  </Typography>
);

// refreshKey changes when a new comment arrives (indicator poll), so an open thread reloads
const _BugDetails = ({ bugId, chatId, step, currentUserId, refreshKey, ...props }: any) => {
  const [actualChatId, setActualChatId] = useState(chatId);
  const hasAttachments = props.imageFilePaths?.length > 0 || props.videoFilePaths?.length > 0;

  // Older bugs get their comment thread on first open
  useEffect(() => {
    validateBugChat(+bugId).then((res) => {
      if (!chatId) {
        setActualChatId(res?.chat);
      }
    });
  }, [bugId, chatId]);

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: colors.textGrey, mb: 2.5 }}>
        {step ? `Step ID: ${step} · ` : ''}Posted {moment(props.createdAt).format('DD MMM YYYY, HH:mm')}
      </Typography>

      <SectionTitle>Details</SectionTitle>
      <Field label="Device" value={props.deviceName} />
      <Field label="Steps to reproduce" value={props.stepsToReproduce} />
      <Field label="Expected result" value={props.expectedResult} />
      <Field label="Actual result" value={props.actualResult} />
      <Field label="Additional info" value={props.comment} />

      <Divider sx={{ my: 2 }} />
      <SectionTitle>Attachments</SectionTitle>
      {hasAttachments ? (
        <BugAttachments imageFilePaths={props.imageFilePaths} videoFilePaths={props.videoFilePaths} />
      ) : (
        <Typography variant="body2" color="textSecondary">No attachments</Typography>
      )}

      <Divider sx={{ my: 2 }} />
      <SectionTitle>Comments</SectionTitle>
      {actualChatId && <ChatView user={currentUserId} chat={actualChatId} refreshKey={refreshKey} />}
    </Box>
  );
};

export const BugDetails = connect((state: any) => ({ currentUserId: state.user?.id }))(_BugDetails);
